<template>
  <div class="ApplicationList--Section">
    <div class="ApplicationListSection--TitleRow">
      <span class="ApplicationListSection--Title">{{ title }}</span>
      <div class="ApplicationListSection--Search">
        <Transition name="slide-fade">
          <el-input
            class="el-input-extra-small"
            v-if="searchEnabled"
            size="mini"
            v-model="searchValue"
          />
        </Transition>
        <el-button
          class="el-button-extra-small"
          :icon="searchEnabled ? 'el-icon-close' : 'el-icon-search'"
          size="small"
          empty
          @click="toggleSearch"
        ></el-button>
      </div>
    </div>
    <div
      class="ApplicationListSection--Applications"
      v-if="applications.length"
      v-loading="isLoading"
    >
      <el-card
        class="ApplicationListSectionApplications--Application"
        :body-style="applicationCardStyle"
        v-for="(app, index) in applications"
        :key="index"
        @click.native="openDetails(app)"
      >
        <div class="ApplicationListApplication--Info">
          <span class="ApplicationListApplication--Title">
            {{ app.name }}
          </span>
          <el-tag
            effect="plain"
            size="mini"
            type="success"
            class="ApplicationListApplication--AppLabel"
            v-if="isApp(app)"
          >
            {{ getApplicationTypeName(app) }}
          </el-tag>
          <el-badge
            is-dot
            class="ApplicationListApplication--Status"
            :class="getAppBadgeClass(app)"
          ></el-badge>
          <span
            class="ApplicationListApplication--StatusText"
            :class="getAppBadgeClass(app)"
          >
            {{ getAppBadgeText(app) }}
          </span>
          <el-popover
            placement="bottom"
            trigger="hover"
            v-if="isChannelLegacy(app, 'push')"
          >
            <el-row
              class="ApplicationListApplicationChannel--Error-Content"
              type="flex"
              align="center"
              justify="start"
            >
              <span>
                {{ $t('ApplicationsChannels_AndroidLegacyNotice_Content') }}
                <a
                  href="https://docs.xtremepush.com/docs/android-push-notifications"
                  target="_blank"
                >
                  {{ $t('ApplicationsChannels_AndroidLegacyNotice_LinkText') }}
                </a>
              </span>
            </el-row>
            <el-tag
              slot="reference"
              size="default"
              type="danger"
              class="ApplicationListApplication--LegacyWarning"
            >
              {{ $t('ApplicationsChannels_AndroidLegacyNotice_Title') }}
            </el-tag>
          </el-popover>
        </div>
        <div class="ApplicationListApplication--Channel">
          <el-popover
            placement="bottom"
            trigger="hover"
            v-if="isChannelStatusError(app, 'push')"
          >
            <el-button
              slot="reference"
              class="ApplicationListApplication--ErrorButton"
            >
              <div class="ApplicationListApplication--ErrorButtonContent">
                <img src="@/img/icon/error.svg" alt="Error icon" />
                <span class="ApplicationListApplicationChannel--Title">
                  {{
                    $t('ApplicationsChannels_ChannelsBlock_PushChannelTitle')
                  }}
                </span>
              </div>
            </el-button>
            <el-row
              class="ApplicationListApplicationChannel--ErrorTitle"
              type="flex"
              align="center"
              justify="start"
            >
              <img src="@/img/icon/error.svg" alt="Error icon" />
              <span>
                {{ getChannelsStatusError(app, 'push', true) }}
              </span>
            </el-row>
            <el-row
              class="ApplicationListApplicationChannel--Error-Content"
              type="flex"
              align="center"
              justify="start"
            >
              <span>
                {{ getChannelsStatusError(app, 'push', false) }}
              </span>
            </el-row>
          </el-popover>
          <template v-else>
            <el-badge
              is-dot
              class="ApplicationListApplication--Status"
              :class="getAppChannelBadgeClass(app, 'push')"
            ></el-badge>
            <span class="ApplicationListApplicationChannel--Title">
              {{ $t('ApplicationsChannels_ChannelsBlock_PushChannelTitle') }}
            </span>
          </template>
        </div>
        <div
          v-if="appTypes === applicationTypes"
          class="ApplicationListApplication--Channel"
        >
          <el-badge
            is-dot
            class="ApplicationListApplication--Status"
            :class="getAppChannelBadgeClass(app, 'inapp')"
          ></el-badge>
          <span class="ApplicationListApplicationChannel--Title">
            {{ $t('ApplicationsChannels_ChannelsBlock_InAppChannelTitle') }}
          </span>
        </div>
        <div v-else class="ApplicationListApplication--Channel">
          <el-badge
            is-dot
            class="ApplicationListApplication--Status"
            :class="getAppChannelBadgeClass(app, 'onsite')"
          ></el-badge>
          <span class="ApplicationListApplicationChannel--Title">
            {{ $t('ApplicationsChannels_ChannelsBlock_OnSiteChannelTitle') }}
          </span>
        </div>
        <div class="ApplicationListApplication--Channel">
          <el-badge
            is-dot
            class="ApplicationListApplication--Status"
            :class="getAppChannelBadgeClass(app, 'inbox')"
          ></el-badge>
          <span class="ApplicationListApplicationChannel--Title">
            {{ $t('ApplicationsChannels_ChannelsBlock_InboxChannelTitle') }}
          </span>
        </div>
      </el-card>
    </div>
    <div
      v-else
      v-loading="isLoading"
      class="ApplicationListSection--ApplicationsNotFound"
    >
      <el-card :body-style="notFoundCardStyle">
        <el-row
          class="ApplicationListSectionApplicationsNotFound--Message"
          type="flex"
          align="center"
          justify="center"
        >
          {{
            searchEnabled
              ? $t('ApplicationsChannels_ApplicationsNotFound')
              : $t('ApplicationsChannels_NoApps')
          }}
        </el-row>
      </el-card>
    </div>
    <el-pagination
      v-if="pages > 1"
      background
      class="paginator"
      :layout="paginationLayout"
      :page-count="pages"
      :current-page="currentPage"
      :disabled="isLoading"
      @current-change="pageChange"
    >
      <template>
        <ul class="el-pager">
          <li class="number active">{{ currentPage }}</li>
        </ul>
      </template>
    </el-pagination>
  </div>
</template>

<script>
import { projectEndpoint, projectRoute } from '@/core/helpers'
import RestApi from '@/core/services/RestApi'
import { ApplicationChannelStatuses } from '@/modules/account/enum/ApplicationChannelStatusEnum'
import { ApplicationTypes } from '@/modules/account/enum/ApplicationEnum'
import { debounce } from 'lodash'

export default {
  name: 'ApplicationSection',

  props: {
    appTypes: {
      type: String,
      required: true
    },

    title: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      isLoading: false,
      searchEnabled: false,
      searchValue: '',
      currentPage: 1,
      pages: 1,
      paginationLayout: 'prev, pager, next',
      applicationCardStyle: {
        padding: '0',
        display: 'flex',
        alignItems: 'center'
      },
      notFoundCardStyle: {
        ...this.applicationCardStyle,
        justifyContent: 'center'
      },
      applications: []
    }
  },

  mounted() {
    this.fetch()
  },

  computed: {
    applicationsEndpoint() {
      const params = {
        page: this.currentPage,
        'per-page': 5,
        type: this.appTypes,
        expand: 'channelsStatuses'
      }

      if (this.searchEnabled && this.searchValue.trim() !== '') {
        params.name = this.searchValue.trim()
      }

      return projectEndpoint({
        method: 'get',
        url: 'applications',
        params: params
      })
    },

    applicationTypes() {
      return [ApplicationTypes.android, ApplicationTypes.ios].join(',')
    }
  },

  watch: {
    searchEnabled(newValue) {
      if (!newValue && this.searchValue.trim() !== '') this.fetch()
    },
    currentPage: debounce(function() {
      this.fetch()
    }, 300),
    searchValue: debounce(function() {
      this.currentPage = 1
      this.fetch()
    }, 2000)
  },

  methods: {
    fetch() {
      this.isLoading = true
      RestApi(this.applicationsEndpoint)
        .then(({ data, headers }) => {
          this.applications = data
          this.isLoading = false
          this.pages = parseInt(headers['x-pagination-page-count'] || '1')
        })
        .catch(error => {
          this.isLoading = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    getAppBadgeClass(app) {
      return {
        'ApplicationListApplication--Status__Activated': app.activated,
        'ApplicationListApplication--Status__NotActivated': !app.activated,
        'ApplicationListApplication--Status__IsApp': this.isApp(app)
      }
    },

    getAppBadgeText(app) {
      return app.activated
        ? this.$t('ApplicationsChannels_SDK_Active')
        : this.$t('ApplicationsChannels_SDK_Not_Active')
    },

    getAppChannelBadgeClass(app, channel) {
      if (
        !app.hasOwnProperty('channelsStatuses') ||
        !app.channelsStatuses.hasOwnProperty(channel)
      ) {
        return {
          'ApplicationListApplicationChannel--Status__Error': true
        }
      }

      let channelStatus = app.channelsStatuses[channel]['status']

      return {
        'ApplicationListApplicationChannel--Status__Activated':
          channelStatus === ApplicationChannelStatuses.active,
        'ApplicationListApplicationChannel--Status__Error':
          channelStatus === ApplicationChannelStatuses.error,
        'ApplicationListApplicationChannel--Status__NotActivated':
          channelStatus === ApplicationChannelStatuses.inactive
      }
    },

    isChannelLegacy(app, channel) {
      if (
        !app.hasOwnProperty('channelsStatuses') ||
        !app.channelsStatuses.hasOwnProperty(channel)
      ) {
        return false
      }

      return app.channelsStatuses[channel]['isLegacy']
    },

    isChannelStatusError(app, channel) {
      if (
        !app.hasOwnProperty('channelsStatuses') ||
        !app.channelsStatuses.hasOwnProperty(channel)
      ) {
        return true
      }

      let channelsStatus = app.channelsStatuses[channel]['status']

      return channelsStatus === ApplicationChannelStatuses.error
    },

    getChannelsStatusError(app, channel, isTitle) {
      if (
        !app.hasOwnProperty('channelsStatuses') ||
        !app.channelsStatuses.hasOwnProperty(channel)
      ) {
        return ''
      }

      let errorKey = app.channelsStatuses[channel]['error']
      errorKey += isTitle ? 'Title' : 'Description'

      return this.$t(errorKey, {
        app_type: app.type === ApplicationTypes.web ? 'website' : 'app'
      })
    },

    getWebsiteBadgeClass(app) {
      return {
        'ApplicationListApplication--Status__Activated': app.activated,
        'ApplicationListApplication--Status__NotActivated': !app.activated
      }
    },

    openDetails(app) {
      this.$router.push(
        projectRoute({
          name: 'ApplicationChannels',
          query: { id: app.id }
        })
      )
    },

    pageChange(page) {
      this.currentPage = page
    },

    prevPage() {
      if (this.currentPage <= 1) return

      this.currentPage--
    },

    nextPage() {
      if (this.currentPage >= this.pages) return

      this.currentPage++
    },

    toggleSearch() {
      this.searchEnabled = !this.searchEnabled
      this.searchValue = ''
    },

    isApp(app) {
      return app.type !== ApplicationTypes.web
    },

    getApplicationTypeName(app) {
      return app.type === ApplicationTypes.ios
        ? this.$t('ApplicationsList_App-Type-IOS')
        : this.$t('ApplicationsList_App-Type-Android')
    }
  }
}
</script>

<style scoped>
.ApplicationList--Section:not(:first-of-type) {
  margin-top: 40px;
}

.ApplicationListSection--TitleRow {
  display: flex;
  align-items: center;
}

.ApplicationListSection--Title {
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #4b515f;
}

.ApplicationListSection--Search {
  margin-left: 16px;
}

.ApplicationListSectionApplications--Application {
  margin-top: 16px;
}

.ApplicationListSectionApplications--Application:hover {
  cursor: pointer;
}

.ApplicationListSectionApplications--Application:not(:first-of-type) {
  margin-top: 8px;
}

.ApplicationListApplication--Info {
  display: flex;
  align-items: center;
  flex: 0 0 50%;
}

.ApplicationListApplication--Icon {
  width: 32px;
  height: 32px;
  margin-left: 16px;
  border-radius: 8px;
}

.ApplicationListApplication--Title,
.ApplicationListApplicationChannel--Title,
.ApplicationListApplication--StatusText {
  margin-left: 16px;
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  color: #4b515f;
}

.ApplicationListApplication--ErrorButton {
  border: none;
  padding: 0 0 0 13px;
}

.ApplicationListApplication--ErrorButton,
.ApplicationListApplication--ErrorButtonContent {
  display: flex;
}

.ApplicationListApplication--ErrorButton:hover {
  background: #f2f5f9;
}

.ApplicationListApplication--ErrorButton
  .ApplicationListApplicationChannel--Title {
  margin-left: 10px;
  color: #ed1d27;
}

.ApplicationListApplicationChannel--ErrorTitle {
  color: #ed1d27;
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
  margin-left: unset !important;
  margin-right: unset !important;
  margin-bottom: 10px;
  max-width: 300px;
}

.ApplicationListApplicationChannel--ErrorTitle img {
  margin-right: 10px;
}

.ApplicationListApplicationChannel--Error-Content {
  color: #5c6374;
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
  margin-left: unset !important;
  margin-right: unset !important;
  max-width: 300px;
  word-break: break-word;
  text-align: left;
}

.ApplicationListApplication--Status {
  display: flex;
  margin-left: 16px;
}

.ApplicationListApplication--Status__IsApp,
.ApplicationListApplication--AppLabel,
.ApplicationListApplication--LegacyWarning {
  margin-left: 8px;
}

.ApplicationListApplication--AppLabel {
  color: #7e8596;
  border-color: #7e8596;
}

.ApplicationListApplication--StatusText {
  margin-left: 5px;
}

>>> .ApplicationListApplication--Status__Activated .el-badge__content,
>>> .ApplicationListApplicationChannel--Status__Activated .el-badge__content {
  background-color: #1fca10;
}

>>> .ApplicationListApplication--Status__NotActivated .el-badge__content,
>>> .ApplicationListApplicationChannel--Status__NotActivated
  .el-badge__content {
  border: 2px solid #c2c7d3;
  background-color: #fff;

  height: 6px;
  width: 6px;
}

.ApplicationListApplication--StatusText.ApplicationListApplication--Status__Activated {
  color: #1fca10;
}

.ApplicationListApplication--StatusText.ApplicationListApplication--Status__NotActivated {
  color: #c2c7d3;
}

>>> .ApplicationListApplicationChannel--Status__Error .el-badge__content {
  background-color: red;
}

.ApplicationListApplication--Channel {
  display: flex;
  align-items: center;
  flex: 0 0 calc((100% / 6) - 49px);

  padding: 21px 24px;

  border-left: 1px solid #ebeef5;
}

.el-button-extra-small {
  padding: 5px;
}

>>> .el-input-extra-small .el-input__inner {
  height: 24px;
  line-height: 24px;
}

.ApplicationListSection--Search {
  display: flex;
}

.ApplicationListSection--ApplicationsNotFound {
  margin-top: 16px;
}

.ApplicationListSectionApplicationsNotFound--Message {
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  color: #4b515f;
}

.paginator {
  display: flex;
  justify-content: center;
  margin: 20px 0;
}

.popover-content {
  word-wrap: break-word;
  white-space: normal;
  overflow-wrap: break-word;
  hyphens: auto;
}

>>> .el-pagination.is-background .el-pager li:not(.disabled).active {
  background-color: #1d87c8;
}

>>> .el-pagination.is-background .btn-prev,
>>> .el-pagination.is-background .btn-next,
>>> .el-pagination.is-background .el-pager li {
  background-color: #fff;
}
</style>



// WEBPACK FOOTER //
// src/modules/account/components/ApplicationSection.vue